import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';
import { projects } from '../../mocks/projects';
import { getBreadcrumbSchema } from '../../utils/structuredData';
import { getCategoryName, getImageUrl } from '../../utils/seo';

export default function Projeler() {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState<string>('tümü');

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      setSelectedCategory(category);
    }
  }, [searchParams]);

  const categories = ['tümü', 'ofis', 'konut', 'ticari'];

  const filteredProjects = selectedCategory === 'tümü'
    ? projects
    : projects.filter(project => project.category === selectedCategory);

  const categoryName = getCategoryName(selectedCategory);
  const pageTitle = selectedCategory === 'tümü' 
    ? 'Projeler | Studio Asım Mete'
    : `${categoryName} Projeleri | Studio Asım Mete`;
  const pageDescription = selectedCategory === 'tümü'
    ? 'Studio Asım Mete\'nin ofis, konut ve ticari mekan tasarım projelerini keşfedin. İstanbul\'da minimalist ve lüks iç mimari çözümler.'
    : `Studio Asım Mete'nin ${categoryName.toLowerCase()} kategorisindeki iç mimari tasarım projelerini keşfedin. İstanbul'da uzman tasarım hizmetleri.`;
  const canonicalUrl = selectedCategory === 'tümü' ? '/projeler' : `/projeler?category=${selectedCategory}`;

  const breadcrumbItems = [
    { name: 'Anasayfa', url: '/' },
    { name: 'Projeler', url: '/projeler' }
  ];
  if (selectedCategory !== 'tümü') {
    breadcrumbItems.push({ name: categoryName, url: `/projeler?category=${selectedCategory}` });
  }

  return (
    <div className="bg-[#1c1612] min-h-screen">
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`${categoryName.toLowerCase()} projeleri, iç mimari projeler, studio asım mete, ${selectedCategory === 'konut' ? 'konut tasarımı' : selectedCategory === 'ofis' ? 'ofis tasarımı' : 'ticari mekan tasarımı'}, istanbul`}
        canonical={canonicalUrl}
        structuredData={getBreadcrumbSchema(breadcrumbItems)}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden -mt-20 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${getImageUrl('/sd-evi/2.png')}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#0e0c0a]/80 via-[#0e0c0a]/70 to-[#0e0c0a]/80"></div>
        </div>
        
        <div className="relative z-[60] text-center px-6">
          <h1 className="text-6xl md:text-7xl font-light text-white tracking-wider mb-4 font-serif">
            Projeler
          </h1>
          <p className="text-xl text-white font-light font-serif">
            Mekansal formda sessiz otorite
          </p>
        </div>
      </section>

      {/* Filter Section */}
      <section className="py-12 px-6 bg-[#0e0c0a]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-8 py-3 rounded-sm text-xs tracking-widest transition-all duration-300 whitespace-nowrap cursor-pointer font-sans uppercase ${
                  selectedCategory === category
                    ? 'bg-[#c8b89a] text-[#1c1612]'
                    : 'bg-transparent border border-[#3d3020] text-white hover:border-[#c8b89a] hover:text-white'
                }`}
              >
                {category.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 px-6 bg-[#1c1612]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-start">
            {filteredProjects.map((project) => (
              <Link key={project.id} to={`/proje/${project.id}`} className="group cursor-pointer w-full">
                <div className="relative h-[400px] overflow-hidden rounded-sm mb-4">
                  <img
                    src={getImageUrl(project.image)}
                    alt={project.title}
                    className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 ${
                      project.id === 5 || project.id === 6 ? 'object-center' : 'object-left-bottom'
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e0c0a]/90 via-[#0e0c0a]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                </div>
                <h3 className="text-2xl font-light text-white tracking-wide mb-2 font-serif">
                  {project.title}
                </h3>
                <p className="text-white font-light text-sm tracking-wide font-sans">
                  {project.location} • {project.year}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
